import React from 'react'
import { useTheme } from '@mui/material/styles'
import { Card, CardContent, Stack, Box, Typography, Avatar, IconButton } from '@mui/material'
import { LocationOn as LocationOnIcon } from '@mui/icons-material'

const HospitalCard = ({ hospital }) => {
  const theme = useTheme()

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 1,
        boxShadow: theme.palette.shadows.light,
        '&:hover': {
          boxShadow: theme.palette.shadows.medium,
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Hospital Name and Location */}
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ color: theme.palette.primary.dark, fontWeight: 'medium', mb: 0.5 }}
            >
              {hospital.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon fontSize='small' color='action' />
              <Typography variant='body2' color='text.secondary'>
                {hospital.city}, {hospital.state}
              </Typography>
            </Box>
          </Box>

          {/* Description */}
          {hospital.about && (
            <Typography
              variant='body2'
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.8rem',
                lineHeight: 1.3,
              }}
            >
              {hospital.about}
            </Typography>
          )}

          {/* Recruiter Contact */}
          {hospital.employerUser && (
            <Box>
              <Typography
                variant='caption'
                sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
              >
                Contact
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[50],
                  '&:hover': {
                    backgroundColor: theme.palette.grey[100],
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '0.8rem',
                  }}
                >
                  {hospital.employerUser.firstName[0]}
                  {hospital.employerUser.lastName[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 'medium',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {hospital.employerUser.firstName} {hospital.employerUser.lastName}
                  </Typography>
                  {hospital.employerUser.showEmail && (
                    <Typography
                      variant='caption'
                      sx={{ color: theme.palette.text.secondary, display: 'block' }}
                    >
                      {hospital.employerUser.email}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {hospital.employerUser.showEmail && (
                    <IconButton
                      size='small'
                      sx={{
                        color: theme.palette.primary.dark,
                        '&:hover': { backgroundColor: theme.palette.primary.light },
                      }}
                      onClick={() => window.open(`mailto:${hospital.employerUser.email}`)}
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z'
                          fill='currentColor'
                        />
                      </svg>
                    </IconButton>
                  )}
                  {hospital.employerUser.showPhone && hospital.employerUser.phone && (
                    <IconButton
                      size='small'
                      sx={{
                        color: theme.palette.primary.dark,
                        '&:hover': { backgroundColor: theme.palette.primary.light },
                      }}
                      onClick={() => window.open(`tel:${hospital.employerUser.phone}`)}
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z'
                          fill='currentColor'
                        />
                      </svg>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default HospitalCard
